import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Search, X, Filter, SlidersHorizontal,
  BookOpen, ClipboardList, Zap, BarChart2,
  Calendar, Repeat, CheckSquare, GitBranch,
  FileText, ArrowLeft, ChevronRight, ArrowRight,
  Tag, Shield, Star, Award, Sparkles
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import PageLayout from '../../components/layout/PageLayout';
import {
  getLibraryItems,
  ITEM_TYPES,
  ITEM_STATUSES,
  EVIDENCE_LEVELS,
  SPORT_LABELS,
} from '../../services/libraryService';

// ─── Icon map ────────────────────────────────────────────────────────
const ICON_MAP = {
  'clipboard-list': ClipboardList,
  'zap':            Zap,
  'bar-chart-2':    BarChart2,
  'book-open':      BookOpen,
  'calendar':       Calendar,
  'repeat':         Repeat,
  'check-square':   CheckSquare,
  'git-branch':     GitBranch,
};

// ─── Sort options ────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { key: 'recent',    label: 'Mais recentes' },
  { key: 'az',        label: 'A → Z' },
  { key: 'evidence',  label: 'Maior evidência' },
];

// ─── Filter Chip ─────────────────────────────────────────────────────
const FilterChip = ({ label, active, onClick, onClear }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold
                border transition-all duration-150 ${
                  active
                    ? 'text-white'
                    : 'hover:bg-gray-50'
                }`}
    style={
      active
        ? { backgroundColor: VUKZ.primary, borderColor: VUKZ.primary, color: '#fff' }
        : { borderColor: VUKZ.border, color: VUKZ.text }
    }
  >
    {label}
    {active && onClear && (
      <X className="w-3 h-3 ml-0.5 hover:opacity-70"
         onClick={(e) => { e.stopPropagation(); onClear(); }} />
    )}
  </button>
);

// ─── Result Card ─────────────────────────────────────────────────────
const ResultCard = ({ item, onClick }) => {
  const typeMeta = ITEM_TYPES[item.item_type?.toLowerCase()] || ITEM_TYPES.protocol;
  const statusMeta = ITEM_STATUSES[item.status?.toLowerCase()] || ITEM_STATUSES.draft;
  const evidenceMeta = EVIDENCE_LEVELS[item.evidence_level] || null;
  const TypeIcon = ICON_MAP[typeMeta.icon] || FileText;

  return (
    <button
      onClick={() => onClick(item)}
      className="group bg-white rounded-2xl border overflow-hidden
                 hover:shadow-md transition-all duration-200 text-left w-full"
      style={{ borderColor: VUKZ.border }}
    >
      <div className="flex gap-4 p-5">
        {/* Icon column */}
        <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center"
             style={{ backgroundColor: VUKZ.primaryLight }}>
          <TypeIcon className="w-5 h-5" style={{ color: VUKZ.primary }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Top row */}
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: VUKZ.textLight }}>
              {typeMeta.label}
            </span>
            <span className="text-xs" style={{ color: VUKZ.textLight }}>·</span>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusMeta.dotColor }} />
              <span className="text-xs" style={{ color: VUKZ.textLight }}>{statusMeta.label}</span>
            </div>
          </div>

          {/* Title */}
          <h4 className="text-sm font-bold mb-1 line-clamp-1
                         group-hover:underline underline-offset-2 decoration-1"
              style={{ color: VUKZ.text }}>
            {item.title}
          </h4>

          {/* Description */}
          {item.description && (
            <p className="text-xs line-clamp-2 mb-2" style={{ color: VUKZ.textLight }}>
              {item.description}
            </p>
          )}

          {/* Bottom pills */}
          <div className="flex flex-wrap items-center gap-2">
            {item.sport && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                {SPORT_LABELS[item.sport] || item.sport}
              </span>
            )}
            {evidenceMeta && (
              <span className="text-xs font-black px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: VUKZ.primaryLight, color: evidenceMeta.color }}>
                {evidenceMeta.badge}
              </span>
            )}
            {item.category && (
              <span className="text-xs px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.textLight }}>
                {item.category}
              </span>
            )}
          </div>
        </div>

        {/* Arrow */}
        <ArrowRight className="w-4 h-4 flex-shrink-0 self-center opacity-0
                               group-hover:opacity-100 transition-opacity"
                    style={{ color: VUKZ.primary }} />
      </div>
    </button>
  );
};

// ─── Sidebar Facet Group ─────────────────────────────────────────────
const FacetGroup = ({ title, options, activeValue, onSelect }) => (
  <div className="mb-6">
    <h4 className="text-xs font-bold uppercase tracking-wider mb-3"
        style={{ color: VUKZ.textLight }}>
      {title}
    </h4>
    <div className="space-y-1">
      {options.map(({ key, label, count }) => {
        const active = activeValue === key;
        return (
          <button
            key={key}
            onClick={() => onSelect(active ? '' : key)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm
                       transition-all duration-150 ${
                         active
                           ? 'font-bold'
                           : 'font-medium hover:bg-gray-50'
                       }`}
            style={
              active
                ? { backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }
                : { color: VUKZ.text }
            }
          >
            <span>{label}</span>
            {count != null && (
              <span className="text-xs tabular-nums"
                    style={{ color: VUKZ.textLight }}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

// =====================================================================
// MAIN PAGE
// =====================================================================
const LibrarySearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialQuery = searchParams.get('q') || '';
  const initialType  = searchParams.get('type') || '';
  const initialSort  = searchParams.get('sort') || 'recent';

  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [query, setQuery]         = useState(initialQuery);
  const [activeType, setActiveType]     = useState(initialType);
  const [activeSport, setActiveSport]   = useState('');
  const [activeEvidence, setActiveEvidence] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [sortBy, setSortBy]       = useState(initialSort);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // ── Fetch all items once ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getLibraryItems();
        if (!cancelled) setItems(data);
      } catch (err) {
        console.error('[Search] load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Client-side filtering ─────────────────────────────────────────
  // Future: replace with Azure AI Search backend call
  const filtered = useMemo(() => {
    let result = [...items];

    // Text search
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      result = result.filter(
        (i) =>
          (i.title || '').toLowerCase().includes(q) ||
          (i.description || '').toLowerCase().includes(q) ||
          (i.category || '').toLowerCase().includes(q) ||
          (typeof i.tags_json === 'string' && i.tags_json.toLowerCase().includes(q))
      );
    }

    // Facets
    if (activeType) {
      result = result.filter((i) => (i.item_type || '').toLowerCase() === activeType.toLowerCase());
    }
    if (activeSport) {
      result = result.filter((i) => (i.sport || '').toLowerCase() === activeSport.toLowerCase());
    }
    if (activeEvidence) {
      result = result.filter((i) => i.evidence_level === activeEvidence);
    }
    if (activeStatus) {
      result = result.filter((i) => (i.status || '').toLowerCase() === activeStatus.toLowerCase());
    }

    // Sort
    if (sortBy === 'az') {
      result.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'evidence') {
      result.sort((a, b) => (b.evidence_score || 0) - (a.evidence_score || 0));
    } else {
      result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }

    return result;
  }, [items, query, activeType, activeSport, activeEvidence, activeStatus, sortBy]);

  // ── Facet counts (for sidebar) ─────────────────────────────────────
  const facetCounts = useMemo(() => {
    const types = {}, sports = {}, evidence = {}, statuses = {};
    items.forEach((i) => {
      const t = (i.item_type || '').toLowerCase();
      types[t] = (types[t] || 0) + 1;
      const s = (i.sport || '').toLowerCase();
      if (s) sports[s] = (sports[s] || 0) + 1;
      const e = i.evidence_level || '';
      if (e) evidence[e] = (evidence[e] || 0) + 1;
      const st = (i.status || '').toLowerCase();
      statuses[st] = (statuses[st] || 0) + 1;
    });
    return { types, sports, evidence, statuses };
  }, [items]);

  // ── URL sync ───────────────────────────────────────────────────────
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (activeType)   params.set('type', activeType);
    if (sortBy !== 'recent') params.set('sort', sortBy);
    setSearchParams(params, { replace: true });
  }, [query, activeType, sortBy, setSearchParams]);

  useEffect(() => {
    updateURL();
  }, [updateURL]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateURL();
  };

  const handleItemClick = (item) => {
    navigate(`/library/item/${item.item_id}`);
  };

  const clearAllFilters = () => {
    setQuery('');
    setActiveType('');
    setActiveSport('');
    setActiveEvidence('');
    setActiveStatus('');
    setSortBy('recent');
  };

  const hasActiveFilters = query || activeType || activeSport || activeEvidence || activeStatus;

  // ── Facet option builders ──────────────────────────────────────────
  const typeOptions = Object.entries(ITEM_TYPES).map(([key, val]) => ({
    key, label: val.label, count: facetCounts.types[key] || 0,
  }));

  const sportOptions = Object.entries(SPORT_LABELS)
    .filter(([key]) => facetCounts.sports[key])
    .map(([key, label]) => ({ key, label, count: facetCounts.sports[key] }));

  const evidenceOptions = Object.entries(EVIDENCE_LEVELS).map(([key, val]) => ({
    key, label: val.label, count: facetCounts.evidence[key] || 0,
  }));

  const statusOptions = Object.entries(ITEM_STATUSES).map(([key, val]) => ({
    key, label: val.label, count: facetCounts.statuses[key] || 0,
  }));

  // ── Sidebar content ────────────────────────────────────────────────
  const SidebarContent = () => (
    <>
      <FacetGroup title="Tipo" options={typeOptions}
                  activeValue={activeType} onSelect={setActiveType} />
      {sportOptions.length > 0 && (
        <FacetGroup title="Esporte" options={sportOptions}
                    activeValue={activeSport} onSelect={setActiveSport} />
      )}
      <FacetGroup title="Nível de Evidência" options={evidenceOptions}
                  activeValue={activeEvidence} onSelect={setActiveEvidence} />
      <FacetGroup title="Status" options={statusOptions}
                  activeValue={activeStatus} onSelect={setActiveStatus} />
    </>
  );

  return (
    <PageLayout
      header={{
        title: 'Pesquisar Biblioteca',
        subtitle: `${filtered.length} resultado(s)`,
        actions: (
          <Link to="/library"
                className="inline-flex items-center gap-2 text-sm font-semibold"
                style={{ color: VUKZ.primary }}>
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
        ),
      }}
    >
      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: VUKZ.textLight }} />
          <input
            type="text"
            placeholder="Buscar por título, descrição, tag..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border text-sm font-medium
                       focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4" style={{ color: VUKZ.textLight }} />
            </button>
          )}
        </div>
      </form>

      {/* Active filters + sort */}
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Mobile filter toggle */}
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                       text-xs font-bold border"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filtros
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-xs font-semibold flex items-center gap-1"
              style={{ color: VUKZ.textLight }}
            >
              <X className="w-3 h-3" /> Limpar filtros
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                sortBy === opt.key ? 'text-white' : 'hover:bg-gray-50'
              }`}
              style={
                sortBy === opt.key
                  ? { backgroundColor: VUKZ.primary, borderColor: VUKZ.primary }
                  : { borderColor: VUKZ.border, color: VUKZ.text }
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Layout: sidebar + results */}
      <div className="flex gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-24">
            <SidebarContent />
          </div>
        </aside>

        {/* Mobile filters drawer */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-50 bg-black/40 lg:hidden"
               onClick={() => setShowMobileFilters(false)}>
            <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-6 overflow-y-auto shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold" style={{ color: VUKZ.text }}>Filtros</h3>
                <button onClick={() => setShowMobileFilters(false)}>
                  <X className="w-5 h-5" style={{ color: VUKZ.textLight }} />
                </button>
              </div>
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Results */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-24 rounded-2xl animate-pulse"
                     style={{ backgroundColor: VUKZ.primaryLight }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border"
                 style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}>
              <Search className="w-12 h-12 mx-auto mb-4" style={{ color: VUKZ.border }} />
              <h4 className="text-lg font-bold mb-2" style={{ color: VUKZ.text }}>
                Nenhum resultado encontrado
              </h4>
              <p className="text-sm mb-6" style={{ color: VUKZ.textLight }}>
                {query
                  ? `Nenhum item corresponde a "${query}"`
                  : 'Tente ajustar os filtros'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                             text-sm font-bold transition-all hover:opacity-90"
                  style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
                >
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((item) => (
                <ResultCard key={item.item_id} item={item} onClick={handleItemClick} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default LibrarySearchPage;
